import { PusherService } from "src/app/_services/pusher.service";
import { SessionStorageService } from "src/app/_services/session-storage.service";
import { LocalStorageService } from "src/app/_services/localStorage.service";
// navigation.service.ts
import { Injectable, OnDestroy } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { NavController } from "@ionic/angular";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";

interface NavigationHistoryEntry {
  url: string;
  timestamp: Date;
  method: string;
}

interface NavigationOptions {
  replaceUrl?: boolean;
  queryParams?: any;
  skipHistory?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class NavigationService implements OnDestroy {
  private subscription: Subscription;
  private timer: any;
  private readonly TIMEOUT = 1000 * 60 * 5; // check auth every 5 minutes
  private navigationHistory: NavigationHistoryEntry[] = [];
  private readonly MAX_HISTORY_SIZE = 50; // Keep last 50 navigation entries

  // URLs that should not be saved in navigation history
  private readonly BLOCKED_URLS: string[] = [
    '/login',
    '/register',
    '/email-forgot-password',
    '/reintialiser-mot-de-passe',
    '/verifier-numero-telephone',
    '/logout',
    '/prestataire/profile',
    '/prestataire/update-password',
    '/particulier/profile',
    '/particulier/update-password',

    '/error',
    '/404',
    '/500',
    '/maintenance',
    '/loading',
    '/splash',
    '/auth/callback',
    '/auth/redirect',
    '/api/',
    '/admin/',
    '/debug/',
    '/test/',
    '/temp/',
    '/tmp/',
    '/network-error',
  ];

  // URL patterns that should not be saved (regex patterns)
  private readonly BLOCKED_PATTERNS: RegExp[] = [
    /\/api\//,
    /\/admin\//,
    /\/debug\//,
    /\/test\//,
    /\/temp\//,
    /\/tmp\//,
    /\/auth\/callback/,
    /\/auth\/redirect/,
    /\/error\//,
    /\/loading\//,
    /\/splash\//,
    /\?.*token=/,
    /\?.*auth=/,
    /\?.*session=/,
    /\?.*temp=/,
    /\?.*debug=/
  ];

  constructor(
    private router: Router,
    private pusherService: PusherService,
    private sessionStorageService: SessionStorageService,
    private navController: NavController,
    private localStorageService: LocalStorageService
  ) {
    this.loadFromSessionStorage();
    this.pusherAuth();
    this.startTimer();
    this.subscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigationEnd = event as NavigationEnd;
        this.resetTimer();
        this.logNavigation(navigationEnd.url, 'NavigationEnd');

        // Auto-cleanup if we have too many entries
        if (this.navigationHistory.length > 20) {
          this.cleanupNavigationHistory();
        }
      });

    // Save data when page becomes hidden (user switches tabs) or before unload
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.saveToSessionStorage();
      }
    });

    // Save data before page unload
    window.addEventListener('beforeunload', () => {
      this.saveToSessionStorage();
    });
  }

  private startTimer() {
    this.timer = setTimeout(() => {
      this.pusherAuth();
      this.resetTimer();
    }, this.TIMEOUT);
  }

  private resetTimer() {
    clearTimeout(this.timer);
    this.startTimer();
  }
  pusherAuth() {
    if (!this.localStorageService.isAuth()) {
      return;
    }

    const auth = this.pusherService.echo;
    if (!auth) {
      setTimeout(() => {
        this.pusherService.auth().subscribe({
          next: (res: any) => {
            if (res) {
              this.subscribeToRoleChannel();
              this.subscribeToDiscussionChannel();
            }
          },
          // En mode non authentifié, `auth()` peut ne rien retourner : on ignore silencieusement.
          error: () => {},
        });
      }, 1000);
    } else if (this.pusherService.isRealtimeUnhealthy()) {
      void this.pusherService.ensureRealtimeConnectionOnForeground(false);
    } else {
      this.subscribeToRoleChannel();
      this.subscribeToDiscussionChannel();
    }
  }
  subscribeToRoleChannel() {
    const channel = this.pusherService.roleChannel;
    if (!channel) {
      this.pusherService.subscribeToRoleChannel();
    }
  }
  subscribeToDiscussionChannel() {
    const channel = this.pusherService.discussionChannel;
    if (!channel) {
      this.pusherService.subscribeToDiscussionChannel();
    }
  }

  /**
   * Check if a URL should be blocked from navigation history
   */
  private isUrlBlocked(url: string): boolean {
    // Check exact URL matches
    if (this.BLOCKED_URLS.includes(url)) {
      return true;
    }

    // Check URL patterns
    for (const pattern of this.BLOCKED_PATTERNS) {
      if (pattern.test(url)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Save data to session storage
   */
  private saveToSessionStorage(): void {
    const data = {
      navigationHistory: this.navigationHistory,
      timestamp: new Date().toISOString()
    };
    this.sessionStorageService.saveNavigationHistory(data);
  }

  /**
   * Load data from session storage
   */
  private loadFromSessionStorage(): void {
    const data = this.sessionStorageService.getNavigationHistory();
    if (data && data.navigationHistory && Array.isArray(data.navigationHistory)) {
      // Convert timestamp strings back to Date objects
      this.navigationHistory = data.navigationHistory.map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp)
      }));
    }
  }

  /**
   * Clear session storage data
   */
  private clearSessionStorage(): void {
    this.sessionStorageService.removeItem('navigationServiceData');
  }

  /**
   * Log navigation event to history (with blocking)
   */
  private logNavigation(url: string, method: string, skipHistory: boolean = false): void {
    // Skip logging if URL is blocked or explicitly skipped
    if (skipHistory || this.isUrlBlocked(url)) {
      return;
    }

    // Prevent duplicate consecutive entries
    if (this.navigationHistory.length > 0) {
      const lastEntry = this.navigationHistory[this.navigationHistory.length - 1];
      if (lastEntry.url === url) {
        return;
      }
    }

    // Prevent too frequent navigation events (minimum 2 seconds gap)
    if (this.navigationHistory.length > 0) {
      const lastEntry = this.navigationHistory[this.navigationHistory.length - 1];
      const timeDiff = new Date().getTime() - lastEntry.timestamp.getTime();
      if (timeDiff < 2000) { // 2 seconds
        return;
      }
    }

    const entry: NavigationHistoryEntry = {
      url,
      timestamp: new Date(),
      method
    };

    this.navigationHistory.push(entry);

    // Keep only the last MAX_HISTORY_SIZE entries
    if (this.navigationHistory.length > this.MAX_HISTORY_SIZE) {
      this.navigationHistory = this.navigationHistory.slice(-this.MAX_HISTORY_SIZE);
    }

    // Only save to session storage for completed navigation events
    if (method === 'NavigationEnd') {
      this.saveToSessionStorage();
    }
  }

  /**
   * Get the current navigation history
   */
  getNavigationHistory(): NavigationHistoryEntry[] {
    return [...this.navigationHistory];
  }

  /**
   * Get the current browser history length
   */
  getBrowserHistoryLength(): number {
    return window.history.length;
  }

  /**
   * Log current browser history state
   */
  logBrowserHistoryState(): void {
    console.log('[NavigationService] Browser History State:', {
      length: window.history.length,
      state: window.history.state,
      currentUrl: window.location.href
    });
  }

  /**
   * Log complete navigation information
   */
  logCompleteNavigationInfo(): void {
    console.log('[NavigationService] === Navigation Information ===');
    console.log('Browser History Length:', window.history.length);
    console.log('Current URL:', window.location.href);
    console.log('Navigation History (last 10 entries):',
      this.navigationHistory.slice(-10).map(entry => ({
        url: entry.url,
        method: entry.method,
        timestamp: entry.timestamp.toISOString()
      }))
    );
    console.log('=== End Navigation Information ===');
  }

  /**
   * Get a clean summary of navigation history
   */
  getNavigationSummary(): any {
    const uniqueRoutes = new Set<string>();
    const routeCounts = new Map<string, number>();
    const currentRoute = this.getCurrentRoute();

    this.navigationHistory.forEach(entry => {
      uniqueRoutes.add(entry.url);
      routeCounts.set(entry.url, (routeCounts.get(entry.url) || 0) + 1);
    });

    return {
      totalEntries: this.navigationHistory.length,
      uniqueRoutes: uniqueRoutes.size,
      currentRoute: currentRoute,
      hasCurrentRouteInHistory: this.navigationHistory.some(entry => entry.url === currentRoute),
      mostVisitedRoutes: Array.from(routeCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([url, count]) => ({ url, count })),
      recentRoutes: this.navigationHistory.slice(-5).map(entry => entry.url),
      hasDuplicates: this.navigationHistory.length > uniqueRoutes.size
    };
  }

  /**
   * Clear navigation history
   */
  clearNavigationHistory(): void {
    this.navigationHistory = [];
    this.clearSessionStorage();
  }

  /**
   * Clean up navigation history by removing duplicates and recent entries
   */
  cleanupNavigationHistory(): void {
    const originalLength = this.navigationHistory.length;
    const currentRoute = this.getCurrentRoute();

    // Remove duplicate consecutive entries
    this.navigationHistory = this.navigationHistory.filter((entry, index, array) => {
      if (index === 0) return true;
      return entry.url !== array[index - 1].url;
    });

    // Keep only unique routes (remove duplicates even if not consecutive)
    const uniqueUrls = new Set<string>();
    this.navigationHistory = this.navigationHistory.filter(entry => {
      if (uniqueUrls.has(entry.url)) {
        return false;
      }
      uniqueUrls.add(entry.url);
      return true;
    });

    // Remove current route to prevent cycles
    this.navigationHistory = this.navigationHistory.filter(entry => entry.url !== currentRoute);

    // Save the cleaned history
    this.saveToSessionStorage();
  }

  /**
   * Remove current route from navigation history to prevent cycles
   */
  removeCurrentRouteFromHistory(): void {
    const currentRoute = this.getCurrentRoute();

    // Remove all entries that match the current route
    this.navigationHistory = this.navigationHistory.filter(entry => entry.url !== currentRoute);

    // Save the updated history to session storage
    this.saveToSessionStorage();
  }

  /**
   * Force clear current route from both memory and session storage
   */
  forceClearCurrentRoute(): void {
    const currentRoute = this.getCurrentRoute();

    // Remove from memory
    this.removeCurrentRouteFromHistory();

    // Also clear from session storage completely and reload
    this.clearSessionStorage();
    this.loadFromSessionStorage();
  }

  /**
   * Navigate back to previous page
   */
  back(): void {
    window.history.back();
  }

  /**
   * Navigate forward to next page
   */
  forward(): void {
    window.history.forward();
  }

  /**
 * Navigate to a specific route
 */
  navigateTo(route: string, options?: NavigationOptions): void {
    // Parse the route to separate path and query parameters
    const [path, queryString] = route.split('?');
    let queryParams = options?.queryParams || {};

    // If there are query parameters in the route, parse them
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      urlParams.forEach((value, key) => {
        queryParams[key] = value;
      });
    }

    // Log navigation for history tracking
    if (!options?.skipHistory) {
      this.logNavigation(path, 'navigateTo', options?.skipHistory);
    }

    if (options?.replaceUrl) {
      // Use navigateRoot for replaceUrl to maintain animation compatibility
      // navigateRoot replaces the current route and triggers proper animation
      this.navController.navigateRoot([path], { queryParams: queryParams });
    } else {
      // Use navigateForward for proper forward transition animation
      this.navController.navigateForward([path], { queryParams: queryParams });
    }
  }

  /**
   * Navigate to a specific route and replace current URL
   */
  replace(route: string, queryParams?: any, skipHistory?: boolean): void {
    const [path, queryString] = route.split('?');
    let mergedQueryParams: Record<string, string> = {};

    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      urlParams.forEach((value, key) => {
        mergedQueryParams[key] = value;
      });
    }
    if (
      queryParams != null &&
      typeof queryParams === 'object' &&
      !Array.isArray(queryParams)
    ) {
      mergedQueryParams = { ...mergedQueryParams, ...queryParams };
    }

    // Log navigation for history tracking
    if (!skipHistory) {
      this.logNavigation(path, 'replace', skipHistory);
    }

    const navOpts =
      Object.keys(mergedQueryParams).length > 0
        ? { queryParams: mergedQueryParams }
        : {};
    this.navController.navigateRoot([path], navOpts);
  }

  /**
   * Navigate to home page
   */
  goHome(): void {
    this.navigateTo('/');
  }

  /**
   * Navigate to login page
   */
  goToLogin(): void {
    this.navigateTo('/login');
  }

  /**
   * Navigate to profile page
   */
  goToProfile(): void {
    this.navigateTo('/profile');
  }

  /**
   * Navigate to settings page
   */
  goToSettings(): void {
    this.navigateTo('/settings');
  }

  /**
   * Smart navigation - checks if user can go back, otherwise goes to home
   */
  smartBack(fallbackRoute: string = '/particulier'): void {
    // Avoid "login flash": if user is not authenticated, skip back attempts
    // that can hit guarded routes and bounce to login first.
    if (!this.localStorageService.isAuth()) {
      this.replace(fallbackRoute, undefined, true);
      return;
    }

    const historyLength = this.getBrowserHistoryLength();
    const navigationHistory = this.getNavigationHistory();

    if (historyLength > 1 && navigationHistory.length > 1) {
      // Use our navigation history to go back
      this.goToPreviousPage();
      this.applyUnauthorizedBackFallback(fallbackRoute);
    } else if (historyLength > 1) {
      // Fallback to browser history
      this.back();
      this.applyUnauthorizedBackFallback(fallbackRoute);
    } else {
      
      this.navigateTo(fallbackRoute);
      // this.navigateTo(fallbackRoute);
    }
  }

  /**
   * If back navigation lands on an auth-guard redirection page,
   * force the configured fallback route.
   */
  private applyUnauthorizedBackFallback(fallbackRoute: string): void {
    setTimeout(() => {
      const currentRoute = this.getCurrentRoute();
      if (currentRoute === '/login' || currentRoute === '/') {
        this.replace(fallbackRoute, undefined, true);
      }
    }, 120);
  }

  /**
   * Navigate with query parameters
   */
  navigateWithParams(route: string, params: any, skipHistory?: boolean): void {
    // Log navigation for history tracking
    if (!skipHistory) {
      this.logNavigation(route, 'navigateWithParams', skipHistory);
    }
    // Use navigateForward for proper transition animation
    this.navController.navigateForward([route], { queryParams: params });
  }

  /**
   * Navigate with state data
   */
  navigateWithState(route: string, state: any, skipHistory?: boolean): void {
    // Log navigation for history tracking
    if (!skipHistory) {
      this.logNavigation(route, 'navigateWithState', skipHistory);
    }
    // Use router for state data as NavController doesn't support it directly
    this.router.navigate([route], { state: state });
  }

  /**
   * Navigate back - wrapper for navController.back() with history tracking
   */
  navigateBack(): void {
    this.goToPreviousPage();
  }

  /**
   * Navigate to external URL
   */
  navigateToExternal(url: string, skipHistory?: boolean): void {
    window.open(url, '_blank');
  }

  /**
   * Navigate to external URL in same tab
   */
  navigateToExternalSameTab(url: string, skipHistory?: boolean): void {
    window.location.href = url;
  }

  /**
   * Refresh current page
   */
  refresh(): void {
    window.location.reload();
  }

  /**
   * Get current route
   */
  getCurrentRoute(): string {
    return this.router.url;
  }

  /**
   * Check if current route matches pattern
   */
  isCurrentRoute(route: string): boolean {
    return this.router.url === route;
  }

  /**
   * Check if current route starts with pattern
   */
  isCurrentRouteStartsWith(pattern: string): boolean {
    return this.router.url.startsWith(pattern);
  }

  /**
   * Get query parameters from current route
   */
  getCurrentQueryParams(): any {
    const urlParams = new URLSearchParams(window.location.search);
    const params: any = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  /**
   * Navigate to previous page in history (if exists)
   */
  goToPreviousPage(): void {
    // Always use navController.back() first - this ensures proper backward transition animation
    // Ionic's navController.back() automatically detects the direction and triggers the correct animation
    if (window.history.length > 1) {
      this.navController.back();
      return;
    }

    // Fallback: use our navigation history
    const history = this.getNavigationHistory();
    const currentRoute = this.getCurrentRoute();
    const isCurrentBlocked = this.isUrlBlocked(currentRoute);

    // Remove current route from history to prevent cycles
    this.removeCurrentRouteFromHistory();

    // Get updated history after removal
    const updatedHistory = this.getNavigationHistory();

    if (updatedHistory.length > 0) {
      let previousEntry: NavigationHistoryEntry;

      if (isCurrentBlocked) {
        // If current route is blocked, go to the last entry in history
        previousEntry = updatedHistory[updatedHistory.length - 1];
      } else {
        // If current route is not blocked, go to the last entry (since we removed current)
        previousEntry = updatedHistory[updatedHistory.length - 1];
      }

      // Decode the URL to handle double-encoding issues
      const decodedUrl = decodeURIComponent(previousEntry.url);
      
      // Parse the route to separate path and query parameters
      const [path, queryString] = decodedUrl.split('?');
      let queryParams: any = {};
      
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        urlParams.forEach((value, key) => {
          queryParams[key] = value;
        });
      }

      // For backward navigation, always prefer navController.back() for proper animation
      // If back() is not available, use navigateForward which Ionic will handle correctly
      // based on the navigation stack
      if (window.history.length > 1) {
        // Use back() for proper backward animation
        this.navController.back();
      } else {
        // Fallback: use navigateForward - Ionic's router will detect if this is a backward
        // navigation based on the navigation stack and apply the correct animation direction
        this.navController.navigateForward([path], { queryParams: queryParams });
      }
    } else {
      // If no history, go home
      this.goHome();
    }
  }

  /**
   * Navigate to specific page by index in history
   */
  goToHistoryIndex(index: number): void {
    const history = this.getNavigationHistory();
    if (index >= 0 && index < history.length) {
      const entry = history[index];
      this.navigateTo(entry.url);
    }
  }

  /**
   * Find and navigate to the last occurrence of a specific route
   */
  goToLastOccurrenceOfRoute(route: string): void {
    const history = this.getNavigationHistory();
    const lastIndex = history.map(entry => entry.url).lastIndexOf(route);

    if (lastIndex !== -1) {
      this.goToHistoryIndex(lastIndex);
    } else {
      this.navigateTo(route);
    }
  }

  /**
   * Find and navigate to the first occurrence of a specific route
   */
  goToFirstOccurrenceOfRoute(route: string): void {
    const history = this.getNavigationHistory();
    const firstIndex = history.findIndex(entry => entry.url === route);

    if (firstIndex !== -1) {
      this.goToHistoryIndex(firstIndex);
    } else {
      this.navigateTo(route);
    }
  }

  /**
   * Navigate back to the last page that matches a pattern
   */
  goToLastPageMatchingPattern(pattern: string): void {
    const history = this.getNavigationHistory();
    const matchingEntries = history.filter(entry => entry.url.includes(pattern));

    if (matchingEntries.length > 0) {
      const lastMatch = matchingEntries[matchingEntries.length - 1];
      const lastIndex = history.lastIndexOf(lastMatch);
      this.goToHistoryIndex(lastIndex);
    }
  }

  /**
   * Get the last N pages from navigation history
   */
  getLastPages(count: number = 5): NavigationHistoryEntry[] {
    const history = this.getNavigationHistory();
    return history.slice(-count);
  }

  /**
   * Check if a route exists in navigation history
   */
  hasVisitedRoute(route: string): boolean {
    const history = this.getNavigationHistory();
    return history.some(entry => entry.url === route);
  }

  /**
   * Get the number of times a route has been visited
   */
  getVisitCount(route: string): number {
    const history = this.getNavigationHistory();
    return history.filter(entry => entry.url === route).length;
  }

  /**
   * Navigate to the most frequently visited route in history
   */
  goToMostVisitedRoute(): void {
    const history = this.getNavigationHistory();
    const routeCounts = new Map<string, number>();

    history.forEach(entry => {
      routeCounts.set(entry.url, (routeCounts.get(entry.url) || 0) + 1);
    });

    let mostVisitedRoute = '';
    let maxCount = 0;

    routeCounts.forEach((count, route) => {
      if (count > maxCount) {
        maxCount = count;
        mostVisitedRoute = route;
      }
    });

    if (mostVisitedRoute) {
      this.navigateTo(mostVisitedRoute);
    } else {
      this.goHome();
    }
  }

  /**
   * Navigate back to the last page that was not the current route
   */
  goToLastDifferentPage(): void {
    const history = this.getNavigationHistory();
    const currentRoute = this.getCurrentRoute();

    for (let i = history.length - 2; i >= 0; i--) {
      if (history[i].url !== currentRoute) {
        this.goToHistoryIndex(i);
        return;
      }
    }

    this.goHome();
  }

  /**
   * Navigate to the page that was visited before a specific route
   */
  goToPageBeforeRoute(targetRoute: string): void {
    const history = this.getNavigationHistory();
    const targetIndex = history.findIndex(entry => entry.url === targetRoute);

    if (targetIndex > 0) {
      this.goToHistoryIndex(targetIndex - 1);
    } else {
      this.goHome();
    }
  }

  /**
   * Navigate to the page that was visited after a specific route
   */
  goToPageAfterRoute(targetRoute: string): void {
    const history = this.getNavigationHistory();
    const targetIndex = history.findIndex(entry => entry.url === targetRoute);

    if (targetIndex !== -1 && targetIndex < history.length - 1) {
      this.goToHistoryIndex(targetIndex + 1);
    }
  }

  /**
   * Get navigation statistics
   */
  getNavigationStats(): any {
    const history = this.getNavigationHistory();
    const routeCounts = new Map<string, number>();
    const methodCounts = new Map<string, number>();

    history.forEach(entry => {
      // Count routes
      routeCounts.set(entry.url, (routeCounts.get(entry.url) || 0) + 1);
      // Count methods
      methodCounts.set(entry.method, (methodCounts.get(entry.method) || 0) + 1);
    });

    return {
      totalEntries: history.length,
      uniqueRoutes: routeCounts.size,
      routeCounts: Object.fromEntries(routeCounts),
      methodCounts: Object.fromEntries(methodCounts),
      firstEntry: history[0] || null,
      lastEntry: history[history.length - 1] || null
    };
  }

  /**
   * Add a URL to the blocked list
   */
  addBlockedUrl(url: string): void {
    if (!this.BLOCKED_URLS.includes(url)) {
      this.BLOCKED_URLS.push(url);
    }
  }

  /**
   * Remove a URL from the blocked list
   */
  removeBlockedUrl(url: string): void {
    const index = this.BLOCKED_URLS.indexOf(url);
    if (index > -1) {
      this.BLOCKED_URLS.splice(index, 1);
    }
  }

  /**
   * Add a URL pattern to the blocked patterns list
   */
  addBlockedPattern(pattern: RegExp): void {
    if (!this.BLOCKED_PATTERNS.some(p => p.source === pattern.source)) {
      this.BLOCKED_PATTERNS.push(pattern);
    }
  }

  /**
   * Remove a URL pattern from the blocked patterns list
   */
  removeBlockedPattern(pattern: RegExp): void {
    const index = this.BLOCKED_PATTERNS.findIndex(p => p.source === pattern.source);
    if (index > -1) {
      this.BLOCKED_PATTERNS.splice(index, 1);
    }
  }

  /**
   * Get all blocked URLs
   */
  getBlockedUrls(): string[] {
    return [...this.BLOCKED_URLS];
  }

  /**
   * Get all blocked patterns
   */
  getBlockedPatterns(): RegExp[] {
    return [...this.BLOCKED_PATTERNS];
  }

  /**
   * Check if a URL is currently blocked
   */
  isUrlCurrentlyBlocked(url: string): boolean {
    return this.isUrlBlocked(url);
  }

  /**
   * Navigate to a route without saving to history
   */
  navigateToSilent(route: string, options?: Omit<NavigationOptions, 'skipHistory'>): void {
    this.navigateTo(route, { ...options, skipHistory: true });
  }

  /**
   * Replace current route without saving to history
   */
  replaceSilent(route: string, queryParams?: any): void {
    this.replace(route, queryParams, true);
  }

  /**
   * Navigate with params without saving to history
   */
  navigateWithParamsSilent(route: string, params: any): void {
    this.navigateWithParams(route, params, true);
  }

  /**
   * Force save current state to session storage
   */
  saveCurrentState(): void {
    this.saveToSessionStorage();
  }

  /**
   * Force reload state from session storage
   */
  reloadFromSessionStorage(): void {
    this.loadFromSessionStorage();
  }

  /**
   * Get complete session storage information
   */
  getCompleteSessionStorageInfo(): any {
    return this.sessionStorageService.getStorageInfo();
  }

  /**
   * Save page state to session storage
   */
  savePageState(pageName: string, stateData: any): void {
    this.sessionStorageService.savePageState(pageName, stateData);
  }

  /**
   * Get page state from session storage
   */
  getPageState<T>(pageName: string): T | null {
    return this.sessionStorageService.getPageState<T>(pageName);
  }

  /**
   * Clear page state from session storage
   */
  clearPageState(pageName: string): void {
    this.sessionStorageService.clearPageState(pageName);
  }

  /**
   * Save form data to session storage
   */
  saveFormData(formName: string, formData: any): void {
    this.sessionStorageService.saveFormData(formName, formData);
  }

  /**
   * Get form data from session storage
   */
  getFormData<T>(formName: string): T | null {
    return this.sessionStorageService.getFormData<T>(formName);
  }

  /**
   * Clear form data from session storage
   */
  clearFormData(formName: string): void {
    this.sessionStorageService.clearFormData(formName);
  }

  /**
   * Clear all session storage data
   */
  clearAllSessionStorage(): void {
    this.sessionStorageService.clear();
  }

  /**
   * Get all session storage keys
   */
  getSessionStorageKeys(): string[] {
    return this.sessionStorageService.getAllKeys();
  }

  /**
   * Get session storage info
   */
  getSessionStorageInfo(): any {
    const data = this.sessionStorageService.getNavigationHistory();
    if (data) {
      return {
        hasData: true,
        timestamp: data.timestamp,
        entryCount: data.navigationHistory?.length || 0,
        storageSize: JSON.stringify(data).length
      };
    }
    return { hasData: false };
  }



  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Save data before destroying
    this.saveToSessionStorage();
  }
}
